import React from 'react';

// Custom sprite images — a single image can contain multiple avatars tiled
// horizontally. Each member type is mapped to its slot in the correct sheet.
const SPRITE_4 = 'https://customer-assets-wrfwihn1.emergentagent.net/wingman/e3dd0bb5-cd52-4986-8bcb-0b93e872dd3d/attachments/5b8c42ae431a4c97890459de5fad5d49_3.png';
const SPRITE_2 = 'https://customer-assets-wrfwihn1.emergentagent.net/wingman/e3dd0bb5-cd52-4986-8bcb-0b93e872dd3d/attachments/6d67a97d2e24435e9ab725f0e070a1ff_2.png';
const SPRITE_1 = 'https://customer-assets-wrfwihn1.emergentagent.net/wingman/e3dd0bb5-cd52-4986-8bcb-0b93e872dd3d/attachments/dd5ad583a7c141efa0c95c2c07a95d86_4.png';

/**
 * For a horizontal sprite with `slots` evenly tiled circles, returns the
 * CSS `background-size` + `background-position` that crops to the circle at
 * `index` so it fills a square container.
 */
function spriteStyle(url, slots, index) {
  if (slots <= 1) {
    return {
      backgroundImage: `url(${url})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
    };
  }
  const posX = (index / (slots - 1)) * 100;
  return {
    backgroundImage: `url(${url})`,
    backgroundSize: `${slots * 100}% 100%`,
    backgroundPosition: `${posX}% center`,
    backgroundRepeat: 'no-repeat',
  };
}

// Color-coded badges + sprite avatars for each community member type.
// `sprite`: picks the correct portion of a tiled sprite sheet image.
// `glow`: colored shadow that complements the artwork on dark backgrounds.
const CFG = {
  'Business Owner': {
    sprite: { url: SPRITE_4, slots: 4, index: 0 },
    glow: 'shadow-[0_0_26px_-4px_rgba(16,185,129,0.65)]',
    pillBg: 'bg-emerald-500', pillText: 'text-white',
  },
  'Consumer': {
    sprite: { url: SPRITE_4, slots: 4, index: 1 },
    glow: 'shadow-[0_0_26px_-4px_rgba(239,68,68,0.65)]',
    pillBg: 'bg-neutral-700', pillText: 'text-white',
  },
  'Nonprofit': {
    sprite: { url: SPRITE_4, slots: 4, index: 2 },
    glow: 'shadow-[0_0_26px_-4px_rgba(34,197,94,0.65)]',
    pillBg: 'bg-sky-500', pillText: 'text-white',
  },
  'Youth Leader': {
    sprite: { url: SPRITE_4, slots: 4, index: 3 },
    glow: 'shadow-[0_0_26px_-4px_rgba(244,114,182,0.65)]',
    pillBg: 'bg-rose-500', pillText: 'text-white',
  },
  // Legacy alias — some records still use "Young Leader".
  'Young Leader': {
    sprite: { url: SPRITE_4, slots: 4, index: 3 },
    glow: 'shadow-[0_0_26px_-4px_rgba(244,114,182,0.65)]',
    pillBg: 'bg-rose-500', pillText: 'text-white',
  },
  'Author': {
    sprite: { url: SPRITE_2, slots: 2, index: 0 },
    glow: 'shadow-[0_0_26px_-4px_rgba(245,158,11,0.65)]',
    pillBg: 'bg-amber-500', pillText: 'text-black',
  },
  'Group': {
    sprite: { url: SPRITE_2, slots: 2, index: 1 },
    glow: 'shadow-[0_0_26px_-4px_rgba(99,102,241,0.65)]',
    pillBg: 'bg-indigo-500', pillText: 'text-white',
  },
  'Artist': {
    sprite: { url: SPRITE_1, slots: 1, index: 0 },
    glow: 'shadow-[0_0_26px_-4px_rgba(236,72,153,0.65)]',
    pillBg: 'bg-pink-500', pillText: 'text-white',
  },
};
const DEFAULT = CFG.Consumer;

function cfgFor(type) {
  return CFG[type] || DEFAULT;
}

/**
 * Small circular type badge — a mini version of the member-type avatar that
 * can sit alongside a member's name. Uses the same sprite artwork so the
 * badge language matches the avatar everywhere.
 */
export function MemberTypeBadge({ type, className = '', testId, size = 28 }) {
  const c = cfgFor(type);
  const px = `${size}px`;
  return (
    <div
      data-testid={testId || `member-badge-${(type || '').replace(/\s/g, '-').toLowerCase()}`}
      aria-label={type || 'Member'}
      title={type || 'Member'}
      className={`inline-block rounded-full overflow-hidden border border-white/15 shadow ${className}`}
      style={{ width: px, height: px, ...spriteStyle(c.sprite.url, c.sprite.slots, c.sprite.index) }}
    />
  );
}

/**
 * Circular member avatar. If the user has a personal `avatarUrl` photo it
 * takes priority, otherwise we render the member-type sprite image.
 */
export function MemberAvatarWithBadge({ initial = '?', memberType, avatarUrl = '', size = 48, className = '', showGlow = true }) {
  const c = cfgFor(memberType);
  const px = `${size}px`;
  return (
    <div
      className={`relative transition-transform duration-200 hover:scale-105 hover:z-10 ${className}`}
      style={{ width: px, height: px }}
      data-testid={`member-avatar-${(memberType || 'consumer').replace(/\s/g, '-').toLowerCase()}`}
      aria-label={memberType || 'Member'}
    >
      <div
        className={`w-full h-full rounded-full overflow-hidden border-2 border-white/90 ${showGlow ? c.glow : 'shadow-md'}`}
        style={!avatarUrl ? spriteStyle(c.sprite.url, c.sprite.slots, c.sprite.index) : undefined}
      >
        {avatarUrl ? (
          <img src={avatarUrl} alt="" className="w-full h-full object-cover" />
        ) : null}
      </div>
    </div>
  );
}

export default MemberTypeBadge;
